import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, Save } from 'lucide-react';
import { useAuth } from '@/core/auth/AuthContext';
import { useResources } from '../hooks/useResources';
import { Input, Select } from '@/components/ui';
import type { EstimateResource, ResourceCreatePayload } from '../types/resources.types';
import { supabase } from '@/core/database/supabase';

interface ResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  resource?: EstimateResource;
}

export function ResourceModal({ isOpen, onClose, resource }: ResourceModalProps) {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const { createResource, updateResource, isCreating, isUpdating } = useResources();
  const isSaving = isCreating || isUpdating;

  const [categories, setCategories] = useState<{id: string, name: string, level: number}[]>([]);

  useEffect(() => {
    if (isOpen) {
      // Fetch categories. We'll use inventory_item_groups for now or categories
      supabase.from('categories').select('id, name, level').eq('company_id', companyId)
        .then(({ data }) => setCategories(data || []));
    }
  }, [isOpen, companyId]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ResourceCreatePayload>({
    defaultValues: {
      category_id: '',
      code: '',
      name: '',
      unit: '',
      description: '',
      status: 'active',
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (resource) {
        reset({
          category_id: resource.category_id || '',
          code: resource.code,
          name: resource.name,
          unit: resource.unit || '',
          description: resource.description || '',
          status: resource.status,
        });
      } else {
        reset({
          category_id: '',
          code: '',
          name: '',
          unit: '',
          description: '',
          status: 'active',
        });
      }
    }
  }, [isOpen, resource, reset]);

  if (!isOpen) return null;

  const onSubmit = async (data: ResourceCreatePayload) => {
    const payload = {
      ...data,
      category_id: data.category_id || null,
    };

    if (resource) {
      await updateResource({ id: resource.id, payload });
    } else {
      await createResource(payload);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-white rounded-lg shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">
            {resource ? 'Cập nhật tài nguyên' : 'Thêm mới tài nguyên'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <form id="resource-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                Nhóm / Danh mục
              </label>
              <Select {...register('category_id')} className="w-full sm:text-sm text-xs">
                <option value="">-- Chọn danh mục --</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>
                    {'--'.repeat(c.level ? c.level - 1 : 0)} {c.name}
                  </option>
                ))}
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                  Mã tài nguyên <span className="text-rose-500">*</span>
                </label>
                <Input
                  {...register('code', { required: 'Vui lòng nhập mã' })}
                  placeholder="VD: M001, VL01"
                  className="w-full sm:text-sm text-xs uppercase font-mono"
                />
                {errors.code && <p className="mt-1 text-xs text-rose-500">{errors.code.message}</p>}
              </div>
              <div>
                <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                  Đơn vị tính <span className="text-rose-500">*</span>
                </label>
                <Input
                  {...register('unit', { required: 'Vui lòng nhập đơn vị' })}
                  placeholder="Cái, Kg, Lít..."
                  className="w-full sm:text-sm text-xs"
                />
                {errors.unit && <p className="mt-1 text-xs text-rose-500">{errors.unit.message}</p>}
              </div>
            </div>

            <div>
              <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                Tên tài nguyên <span className="text-rose-500">*</span>
              </label>
              <Input
                {...register('name', { required: 'Vui lòng nhập tên' })}
                placeholder="Máy xúc đào, Xi măng PC40..."
                className="w-full sm:text-sm text-xs"
              />
              {errors.name && <p className="mt-1 text-xs text-rose-500">{errors.name.message}</p>}
            </div>

            <div>
              <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                Mô tả
              </label>
              <Input
                {...register('description')}
                placeholder="Ghi chú về tài nguyên này..."
                className="w-full sm:text-sm text-xs"
              />
            </div>

            <div>
              <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                Trạng thái
              </label>
              <Select {...register('status')} className="w-full sm:text-sm text-xs">
                <option value="active">Sẵn sàng</option>
                <option value="inactive">Vô hiệu hóa</option>
              </Select>
            </div>
          </form>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200 bg-slate-50 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 sm:text-sm text-xs font-medium text-slate-700 hover:bg-slate-200 rounded-md transition-colors"
          >
            Hủy bỏ
          </button>
          <button
            type="submit"
            form="resource-form"
            disabled={isSaving}
            className="inline-flex items-center justify-center gap-2 px-6 py-2 sm:text-sm text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Đang lưu...' : 'Lưu lại'}
          </button>
        </div>
      </div>
    </div>
  );
}
